import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid2 as Grid,
  IconButton,
  Pagination,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  useDeleteOmnitokenMutation,
  useFetchOmnitokensQuery,
  useUpdateOmnitokenMutation,
} from "../../api";
import { useAtom } from "jotai";
import {
  selectedAccountIdAtom,
  selectedOrgIdAtom,
  toastOptionsAtom,
} from "../../atoms";
import { useEffect, useState } from "react";
import { ConfirmationModal, HorizontalStepper } from "../common";
import { theme } from "../../utils";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { InvoicePaymentInitiatePayload, Omnitoken } from "../../types";
import { PaymentMethod, TokenType } from "../../enums";
import { CardInfoTile, ManageCardsToolbar } from "./fragments";
import { HostedPaymentsPage } from "../payment/fragments";

export const ManageCards = () => {
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const isSm = useMediaQuery(theme.breakpoints.only("sm"));
  const isLg = useMediaQuery(theme.breakpoints.only("lg"));
  const isXl = useMediaQuery(theme.breakpoints.only("xl"));
  const isMediumLargeScreen = useMediaQuery("(max-width: 1550px)");
  const [cardToDelete, setCardToDelete] = useState<string>();
  const [paymentMethodToDelete, setPaymentMethodToDelete] =
    useState<PaymentMethod>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(16);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [createPaymentMethod, setCreatePaymentMethod] =
    useState<PaymentMethod>();
  const [savedCards, setSavedCards] = useState<Omnitoken[]>([]);
  const [paginatedPaymentMethods, setPaginatedPaymentMethods] =
    useState<Omnitoken[]>();
  const [selectedOrgId] = useAtom(selectedOrgIdAtom);
  const [selectedAccountId] = useAtom(selectedAccountIdAtom);
  const [, setToastOptions] = useAtom(toastOptionsAtom);
  const fetchOmnitokensQuery = useFetchOmnitokensQuery(
    selectedOrgId,
    selectedAccountId || 0,
    {
      enabled: selectedOrgId && selectedAccountId ? true : false,
    },
  );
  const deleteOmnitokenMutation = useDeleteOmnitokenMutation();
  const updateOmnitokenMutation = useUpdateOmnitokenMutation();
  const authPayload = {
    authTransaction: true,
    accountId: selectedAccountId,
    saveCard: true,
    paymentMethod: createPaymentMethod,
  } as InvoicePaymentInitiatePayload;
  const paginatedCards =
    paginatedPaymentMethods?.filter(
      (item) => item.tokenType === TokenType.Card,
    ) ?? [];

  const paginatedAchAccounts =
    paginatedPaymentMethods?.filter(
      (item) => item.tokenType === TokenType.Ach,
    ) ?? [];
  const handleClose = () => {
    setIsDialogOpen(false);
    fetchOmnitokensQuery.refetch();
  };

  const getPaginatedItems = (
    array: Omnitoken[],
    page: number,
    pageSize: number,
  ) => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return (array || []).slice(startIndex, endIndex);
  };

  const handleCreate = (paymentMethod: PaymentMethod) => {
    setCreatePaymentMethod(paymentMethod);
    setIsDialogOpen(true);
  };
  const handleOpenModal = () => setIsModalOpen(true);

  const handleCloseModal = () => {
    setCardToDelete("");
    setPaymentMethodToDelete(undefined);
    setIsModalOpen(false);
  };

  const handleClickDelete = (id: string) => {
    const paymentMethod = savedCards.find((item) => item.id === id);

    setCardToDelete(id);
    setPaymentMethodToDelete(
      paymentMethod?.tokenType === TokenType.Ach
        ? PaymentMethod.Ach
        : paymentMethod?.tokenType === TokenType.Card
          ? PaymentMethod.Card
          : undefined,
    );

    handleOpenModal();
  };

  const handleConfirmDelete = () => {
    deleteOmnitokenMutation.mutate({
      orgId: selectedOrgId,
      accountId: selectedAccountId,
      id: cardToDelete,
    });

    handleCloseModal();
  };

  const handleLabelUpdate = (id: number, label: string) => {
    updateOmnitokenMutation.mutate({
      orgId: selectedOrgId,
      accountId: selectedAccountId,
      id,
      cardLabel: label,
    });
  };

  useEffect(() => {
    if (isXs) setPageSize(10);
    else if (isSm) setPageSize(10);
    else if (isLg) setPageSize(12);
    else if (isXl) setPageSize(16);
  }, [isXs, isSm, isLg, isXl]);

  useEffect(() => {
    if (savedCards) {
      setPaginatedPaymentMethods(getPaginatedItems(savedCards, page, pageSize));
    }
  }, [savedCards, page, pageSize]);

  useEffect(() => {
    if (fetchOmnitokensQuery.isSuccess) {
      setSavedCards(fetchOmnitokensQuery.data.omnitokens);
    }

    if (fetchOmnitokensQuery.isError) {
      setSavedCards([]);
      setToastOptions({
        open: true,
        message: "Failed to fetch cards",
        severity: "error",
      });
    }
  }, [
    fetchOmnitokensQuery.isSuccess,
    fetchOmnitokensQuery.isError,
    fetchOmnitokensQuery.data,
    fetchOmnitokensQuery.error,
    fetchOmnitokensQuery.isLoading,
    fetchOmnitokensQuery.isFetching,
    fetchOmnitokensQuery.isRefetching,
  ]);

  useEffect(() => {
    if (deleteOmnitokenMutation.isSuccess) {
      fetchOmnitokensQuery.refetch();

      setToastOptions({
        open: true,
        message:
          paymentMethodToDelete === PaymentMethod.Ach
            ? "Bank account deleted successfully"
            : "Card deleted successfully",
        severity: "success",
      });
    }

    if (deleteOmnitokenMutation.isError) {
      setToastOptions({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    }
  }, [
    deleteOmnitokenMutation.isSuccess,
    deleteOmnitokenMutation.isError,
    deleteOmnitokenMutation.isLoading,
    deleteOmnitokenMutation.data,
    deleteOmnitokenMutation.error,
  ]);

  useEffect(() => {
    if (updateOmnitokenMutation.isSuccess) {
      fetchOmnitokensQuery.refetch();

      setToastOptions({
        open: true,
        message: "label updated successfully",
        severity: "success",
      });
    }

    if (updateOmnitokenMutation.isError) {
      setToastOptions({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    }
  }, [
    updateOmnitokenMutation.isSuccess,
    updateOmnitokenMutation.isError,
    updateOmnitokenMutation.isLoading,
    updateOmnitokenMutation.data,
    updateOmnitokenMutation.error,
  ]);

  return (
    <>
      <Box sx={{ width: "100%", height: "calc(100vh - 150px)" }}>
        <Box
          sx={{
            overflow: "auto",
            px: "5px",
            display: "flex",
            width: "100%",
            height: "100%",
            flexGrow: 1,
            flexDirection: "column",
          }}
        >
          <Grid
            container
            direction={"row"}
            spacing={2}
            sx={{
              width: "100%",
              height: "100%",
              flexGrow: 1,
              alignItems:
                fetchOmnitokensQuery.isLoading ||
                fetchOmnitokensQuery.isRefetching ||
                (fetchOmnitokensQuery.isSuccess && !paginatedPaymentMethods) ||
                fetchOmnitokensQuery.isError
                  ? "center"
                  : "none",
              alignContent:
                fetchOmnitokensQuery.isLoading ||
                fetchOmnitokensQuery.isRefetching ||
                (fetchOmnitokensQuery.isSuccess && !paginatedPaymentMethods) ||
                fetchOmnitokensQuery.isError
                  ? "none"
                  : "baseline",
            }}
          >
            {fetchOmnitokensQuery.isLoading ||
            fetchOmnitokensQuery.isFetching ||
            fetchOmnitokensQuery.isRefetching ||
            updateOmnitokenMutation.isLoading ||
            deleteOmnitokenMutation.isLoading ||
            (fetchOmnitokensQuery.isSuccess && !paginatedPaymentMethods) ? (
              <Grid
                size={12}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Grid>
            ) : fetchOmnitokensQuery.isError ? (
              <Grid
                size={12}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                Nothing to show
              </Grid>
            ) : (
              <>
                <Grid size={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 500,
                        color: "#1A1A1A",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                        mb: 0,
                      }}
                    >
                      Card on File
                    </Typography>

                    <ManageCardsToolbar
                      handleCreate={() => handleCreate(PaymentMethod.Card)}
                    />
                  </Box>

                  {paginatedCards.length > 0 ? (
                    <Box
                      sx={{
                        border: "1px solid #D9D9D9",
                        borderRadius: "8px",
                        p: 3,
                        bgcolor: "#fff",
                        boxShadow: "0px 1px 3px rgba(0,0,0,0.08)",
                      }}
                    >
                      <Grid container spacing={2}>
                        {paginatedCards.map((item) => (
                          <Grid
                            key={item.id}
                            size={{
                              lg: 4,
                              md: 6,
                              sm: 6,
                              xs: 12,
                              xl: isMediumLargeScreen ? 3.5 : 3,
                            }}
                          >
                            <CardInfoTile
                              data={item}
                              handleDelete={handleClickDelete}
                              handleLabelUpdate={handleLabelUpdate}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        textAlign: "center",
                        py: 4,
                        color: theme.palette.text.primary,
                      }}
                    >
                      Nothing to show
                    </Typography>
                  )}
                </Grid>

                <Grid size={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                      gap: 2,
                      mt: 4,
                      mb: 1.5,

                      [theme.breakpoints.down("sm")]: {
                        flexDirection: "column",
                        alignItems: "stretch",
                        gap: 1.5,
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 500,
                        color: "#1A1A1A",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                        mb: 0,
                      }}
                    >
                      Bank Account on File
                    </Typography>

                    <ManageCardsToolbar
                      handleCreate={() => handleCreate(PaymentMethod.Ach)}
                      label="Add Bank Account"
                    />
                  </Box>

                  {paginatedAchAccounts.length > 0 ? (
                    <Box
                      sx={{
                        border: "1px solid #D9D9D9",
                        borderRadius: "8px",
                        p: 3,
                        bgcolor: "#fff",
                        boxShadow: "0px 1px 3px rgba(0,0,0,0.08)",
                      }}
                    >
                      <Grid container spacing={2}>
                        {paginatedAchAccounts.map((item) => (
                          <Grid
                            key={item.id}
                            size={{
                              lg: 4,
                              md: 6,
                              sm: 6,
                              xs: 12,
                              xl: isMediumLargeScreen ? 3.5 : 3,
                            }}
                          >
                            <CardInfoTile
                              data={item}
                              handleDelete={handleClickDelete}
                              handleLabelUpdate={handleLabelUpdate}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        textAlign: "center",
                        py: 4,
                        color: theme.palette.text.primary,
                      }}
                    >
                      Nothing to show
                    </Typography>
                  )}
                </Grid>
              </>
            )}
          </Grid>
        </Box>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50px",
          }}
        >
          <Pagination
            count={
              savedCards && savedCards.length > 0
                ? Math.ceil(savedCards.length / pageSize)
                : 1
            }
            page={page}
            onChange={(event: React.ChangeEvent<unknown>, value: number) => {
              setPage(value);
            }}
            color="primary"
            sx={{
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          />
        </Box>
        <ConfirmationModal
          open={isModalOpen}
          onClose={handleCloseModal}
          onConfirm={handleConfirmDelete}
          icon={
            <DeleteOutlineOutlinedIcon
              fontSize="medium"
              sx={{ color: theme.palette.primary.main }}
            />
          }
          title={
            paymentMethodToDelete === PaymentMethod.Ach
              ? "Delete Bank Account"
              : "Delete Card"
          }
          message={
            paymentMethodToDelete === PaymentMethod.Ach
              ? "Are you sure that you want to delete this bank account?"
              : "Are you sure that you want to delete this card?"
          }
        />
      </Box>

      <Dialog
        open={isDialogOpen}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        sx={{
          "& .MuiDialog-paper": {
            width: "100%",
            maxWidth: {
              xs: "calc(100% - 24px)",
              sm: "680px",
              md: "740px",
            },

            minHeight: {
              xs: "calc(100vh - 24px)",
              sm: "600px",
              md: "620px",
            },

            maxHeight: {
              xs: "calc(100vh - 24px)",
              sm: "90vh",
            },

            margin: {
              xs: "12px",
              sm: "24px",
            },

            borderRadius: "8px",
            overflow: "hidden",
          },
        }}
      >
        {!selectedOrgId || !selectedAccountId ? (
          <Grid
            container
            size={12}
            sx={{
              width: "100%",
              height: "100%",
              minHeight: "75vh",
              backgroundColor: "white",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Grid>
        ) : (
          <Box
            sx={{
              width: "100%",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              p: 0,
            }}
          >
            <DialogTitle
              sx={{
                flexShrink: 0,
                p: { xs: 1, sm: 1.5 },
                minHeight: 0,
              }}
            >
              <IconButton
                style={{
                  position: "absolute",
                  top: "20px",
                  right: "20px",
                  zIndex: 1000,
                }}
                onClick={handleClose}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent
              sx={{
                p: 0,
                m: 0,
                minHeight: 0,
                width: "100%",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <HostedPaymentsPage payload={authPayload} orgId={selectedOrgId} />
            </DialogContent>
          </Box>
        )}
      </Dialog>
    </>
  );
};
